import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, Archive, Boxes, Download, Plus, Upload, X } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import InventoryTable from '../components/inventory/InventoryTable';
import { CreateDialog, ImportDialog, RevealDialog } from '../components/inventory/InventoryDialogs';
import { AssignDialog, AssignmentsLog, CapacityView, ProblemsView } from '../components/inventory/InventoryOps';
import { inventoryApi, errorText, downloadBlob } from '../lib/inventoryApi';
import { placementWarningText } from '../lib/inventoryOps';
import { MISSING_ALERT, SUBVIEWS, TABS, columnsFor, loadState, missingAlerts, saveState, serverParams, setStorageScope } from '../lib/inventoryTable';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '@/lib/utils';
import usePageTitle from '../hooks/usePageTitle';

/*
 * The Inventory tab (r16; NecroGrowth docs/01-inventory/01.4-phase1-spec.md section 6): Accounts, Groups, Phones
 * (+ Profiles and, for the admin, PCs), Network, Clients, History (+ Secret access). Every tab is the same
 * InventoryTable driven by lib/inventoryTable.js. The SERVER scopes every row to the viewer: a client sees only its
 * own part and cannot write; this page only hides the controls it could not use.
 *
 * URL: /inventory/:tab?sub=<subview>&f.<field>=<value>&archived=true. Link chips navigate here with f.id / f.groupId
 * / f.clientId, so "click through" is a plain, bookmarkable URL.
 */

/**
 * Where a chip of a given table opens. A chip to ONE row carries archived=all: the row it links to may be archived
 * (every "archived" History row, an account's archived client or group, a phone's archived account), and the list
 * hides archived rows by default - the chip opened "Nothing here yet." (re-review must-fix 8). Count chips (all
 * accounts of a group / client) keep the live view.
 */
export function chipTarget(chip) {
  switch (chip?.table) {
    case 'accounts': case 'groups': case 'phones': case 'network': case 'clients':
      return `/inventory/${chip.table}?f.id=${chip.id}&archived=all`;
    case 'profiles':
      return `/inventory/phones?sub=profiles&f.id=${chip.id}&archived=all`;
    case 'hosts':
      return `/inventory/phones?sub=hosts&f.id=${chip.id}&archived=all`;
    case '__count': {
      const map = {
        'groups:accounts': `/inventory/accounts?f.groupId=${chip.id}`,
        'clients:accounts': `/inventory/accounts?f.clientId=${chip.id}`,
        'clients:phones': `/inventory/phones?f.clientId=${chip.id}`,
      };
      return map[`${chip.from}:${chip.link}`] || null;
    }
    default:
      return null;
  }
}

/**
 * The view a tab opens with: its saved working state, except that a linked-chip URL (f.id, f.groupId ...) drops the
 * saved search, column filters and grouping - otherwise they can hide the very row the chip links to ("Nothing here
 * yet."; review must-fix 8). Sort and hidden columns stay. The saved state is only replaced once the user changes the
 * view.
 */
export function initialView(table, hasLinkFilter) {
  const saved = loadState(table);
  return hasLinkFilter ? { ...saved, q: '', filters: {}, groupBy: '' } : saved;
}

/** The signed-in email, or '' outside an AuthProvider (unit tests render the page bare). */
function useViewerEmail() {
  try {
    return useAuth()?.user?.email || '';
  } catch (e) {
    return '';
  }
}

export default function InventoryPage() {
  usePageTitle('Inventory');
  // INV-7b browser drive: saved views / working state per login (set before any loadState below).
  const viewer = useViewerEmail();
  setStorageScope(viewer);
  const { tab: tabParam } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const tab = TABS.some((t) => t.key === tabParam) ? tabParam : 'accounts';

  const metaQ = useQuery({ queryKey: ['inventory-meta'], queryFn: () => inventoryApi.meta().then((r) => r.data), retry: false });
  const meta = metaQ.data;
  const isAdmin = !!meta?.isAdmin;

  const subs = (SUBVIEWS[tab] || [{ key: tab, label: null, table: tab }]).filter((s) => !s.adminOnly || isAdmin);
  const sub = subs.find((s) => s.key === search.get('sub')) || subs[0];
  // Phase 3: Problems, Capacity and History > Assignments are views of their own, not an InventoryTable.
  const special = TABS.find((t) => t.key === tab)?.special ? tab : sub.special ? sub.key : null;
  const table = special ? null : sub.table;
  const tableMeta = table ? meta?.tables?.[table] : null;

  // Link filters from the URL (f.id, f.groupId, f.clientId ...) and the archived toggle go to the server.
  const linkParams = useMemo(() => {
    const out = {};
    for (const [k, v] of search.entries()) if (k.startsWith('f.')) out[k] = v;
    // 'true' = only archived rows (the toolbar toggle); 'all' = live and archived (a linked chip, must-fix 8).
    const archived = search.get('archived');
    if (archived === 'true' || archived === 'all') out.archived = archived;
    return out;
  }, [search]);
  const showArchived = linkParams.archived === 'true';

  const listQ = useQuery({
    queryKey: ['inventory', table, JSON.stringify(linkParams)],
    queryFn: () => inventoryApi.list(table, { ...linkParams }).then((r) => r.data),
    enabled: !!meta && !!tableMeta,
    retry: false,
  });

  const columns = useMemo(() => columnsFor(table, meta), [table, meta]);
  // A linked-chip URL (f.*) opens on the linked row: it starts from no search, filters or grouping (must-fix 8).
  const linkKey = useMemo(() => JSON.stringify(Object.entries(linkParams).filter(([k]) => k.startsWith('f.'))), [linkParams]);
  const hasLink = linkKey !== '[]';
  const [view, setView] = useState(() => initialView(table, hasLink));
  useEffect(() => { setView(initialView(table, hasLink)); }, [table, hasLink, linkKey, viewer]);
  const changeView = useCallback((v) => { setView(v); saveState(table, v); }, [table]);

  const [reveal, setReveal] = useState(null);
  const [assignRow, setAssignRow] = useState(null);
  // Phase 2 review should-fix 5: the "N phones missing" count comes from the server's Problems board (every phone in
  // view, not only the rows loaded here); "show" filters to the server's missingAlert (missing AND in service).
  const missingQ = useQuery({
    queryKey: ['inventory-problems', 'phone_missing'],
    queryFn: () => inventoryApi.problems({ type: 'phone_missing' }).then((r) => r.data),
    enabled: !!meta && table === 'phones',
    retry: false,
  });
  const [importOpen, setImportOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const setSearch = (patch) => {
    const next = new URLSearchParams(search);
    for (const [k, v] of Object.entries(patch)) {
      if (v == null || v === '') next.delete(k); else next.set(k, v);
    }
    navigate({ search: next.toString() ? `?${next}` : '' });
  };

  const refetch = () => listQ.refetch();

  const onCellSave = async (row, col, value) => {
    try {
      const r = await inventoryApi.update(table, row.id, { [col.field]: value });
      toast.success(`Saved ${col.label}`);
      const warn = placementWarningText(r.data);
      if (warn) {
        // Re-review should-fix 5: saved, but a placement Assign would refuse - say so (the row itself has no such field).
        toast.warning(warn, { duration: 15000 });
        const { warnings, ...saved } = r.data;
        return saved;
      }
      return r.data;
    } catch (e) {
      toast.error(errorText(e, 'Not saved'));
      throw e;
    }
  };

  const onBulkSet = async (ids, col, value) => {
    try {
      const r = await inventoryApi.bulk(table, ids, { [col.field]: value });
      toast.success(`${col.label}: ${r.data.changed} of ${r.data.rows} rows changed`);
      const warn = placementWarningText(r.data);
      if (warn) toast.warning(warn, { duration: 15000 });
      refetch();
    } catch (e) {
      toast.error(errorText(e, 'Bulk edit refused - nothing was changed'));
    }
  };

  const onBulkGroups = async (ids, add, remove) => {
    try {
      const r = await inventoryApi.bulkGroups(ids, add, remove);
      toast.success(`${r.data.changed} group membership change(s)`);
      refetch();
    } catch (e) {
      toast.error(errorText(e, 'Group change refused'));
    }
  };

  const onChipClick = (chip) => {
    const to = chipTarget(chip);
    if (to) navigate(to);
  };

  const onExport = async () => {
    try {
      // INV-7b: the export follows the table's sort too (serverParams sends the server-sortable keys). INV-8: a client's
      // export never sends a sort key its meta does not list (an admin-only field: the server answers 400).
      const params = serverParams(columns, view, linkParams, tableMeta?.fields, isAdmin);
      const r = await inventoryApi.exportCsv(table, params);
      downloadBlob(r.data, `inventory-${table}-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (e) {
      toast.error(errorText(e, 'Export failed'));
    }
  };

  const onArchive = async (row) => {
    if (!window.confirm(`Archive ${row.label}? It disappears from the list (Show archived brings it back).`)) return;
    try { await inventoryApi.archive(table, row.id); toast.success(`Archived ${row.label}`); refetch(); } catch (e) { toast.error(errorText(e)); }
  };
  const onRestore = async (row) => {
    try { await inventoryApi.restore(table, row.id); toast.success(`Restored ${row.label}`); refetch(); } catch (e) { toast.error(errorText(e)); }
  };

  // ----------------------------------------------------------------------------------------- states

  if (metaQ.isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading inventory…</div>;
  }
  if (metaQ.error) {
    const status = metaQ.error?.response?.status;
    return (
      <div className="p-6">
        <PageHeader icon={Boxes} title="Inventory" />
        <p className="text-sm text-muted-foreground" data-testid="inv-unavailable">
          {status === 404 ? 'Inventory is switched off on this server.' : errorText(metaQ.error, 'Inventory is not available.')}
        </p>
      </div>
    );
  }
  if (!isAdmin && !meta?.hasClient) {
    return (
      <div className="p-6">
        <PageHeader icon={Boxes} title="Inventory" />
        <p className="text-sm text-muted-foreground" data-testid="inv-no-client">No inventory is linked to your login yet. Ask your account manager to link it.</p>
      </div>
    );
  }

  const linkFilterEntries = Object.entries(linkParams).filter(([k]) => k.startsWith('f.'));
  const canWrite = isAdmin && tableMeta && !tableMeta.readOnly;
  // Phase 2: phones in service that their PC no longer reports (the server's missingAlert), as one alert above the table.
  const serverMissing = missingQ.data?.problems;
  const missing = table !== 'phones' ? [] : Array.isArray(serverMissing) ? serverMissing : missingAlerts(listQ.data?.rows);
  const missingMinutes = Number(meta?.missingMinutes) || 30;
  const importable = canWrite && (tableMeta?.importKey || []).length > 0;

  return (
    <div className="space-y-3" data-testid="inventory-page">
      <PageHeader
        icon={Boxes}
        title="Inventory"
        description={isAdmin
          ? 'Every account, phone, profile, network and client in one place. Double-click a cell to edit; select rows to bulk edit.'
          : 'Your accounts, phones and network entries. Revealing a password is logged.'}
      />

      <div className="flex flex-wrap gap-1 border-b border-border" role="tablist">
        {TABS.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={t.key === tab}
            onClick={() => navigate(`/inventory/${t.key}`)}
            className={cn('-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              t.key === tab ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground')}>
            {t.label}
          </button>
        ))}
      </div>

      {subs.length > 1 && (
        <div className="inline-flex rounded-lg border border-border p-0.5 text-xs" data-testid="inv-subviews">
          {subs.map((s) => (
            <button key={s.key} type="button" onClick={() => navigate(`/inventory/${tab}${s.key === subs[0].key ? '' : `?sub=${s.key}`}`)}
              className={cn('rounded-md px-2.5 py-1', s.key === sub.key ? 'bg-primary/15 text-foreground' : 'text-muted-foreground hover:text-foreground')}>
              {s.label}
            </button>
          ))}
        </div>
      )}

      {linkFilterEntries.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="inv-link-filter">
          <span className="text-muted-foreground">Linked filter:</span>
          {linkFilterEntries.map(([k, v]) => (
            <span key={k} className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5">
              {k.slice(2)} = {v}
              {/* Clearing the last link also drops a chip's archived=all, so the plain list is the live list again. */}
              <button type="button" aria-label={`Clear ${k}`}
                onClick={() => setSearch({ [k]: null, ...(linkFilterEntries.length === 1 && linkParams.archived === 'all' ? { archived: null } : {}) })}>
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          {linkParams.archived === 'all' && (
            <span className="text-muted-foreground" data-testid="inv-link-archived">(archived rows included)</span>
          )}
        </div>
      )}

      {missing.length > 0 && (
        <button type="button" data-testid="inv-missing-summary"
          onClick={() => changeView({ ...view, filters: { ...(view?.filters || {}), liveness: MISSING_ALERT } })}
          title="Phones in service that their PC has not reported for longer than this. Click to show only them."
          className="inline-flex items-center gap-1.5 rounded-md border border-danger/30 bg-danger/10 px-2.5 py-1 text-xs font-medium text-danger hover:bg-danger/15">
          <AlertTriangle className="h-3.5 w-3.5" />
          {missing.length} phone{missing.length === 1 ? '' : 's'} missing &gt; {missingMinutes} min
          <span className="font-normal underline underline-offset-2">show</span>
        </button>
      )}

      {special === 'problems' && <ProblemsView isAdmin={isAdmin} onChipClick={onChipClick} />}
      {special === 'capacity' && <CapacityView isAdmin={isAdmin} onChipClick={onChipClick} />}
      {special === 'assignments' && <AssignmentsLog isAdmin={isAdmin} onChipClick={onChipClick} />}

      {special ? null : listQ.error ? (
        <p className="text-sm text-danger" role="alert">{errorText(listQ.error)}</p>
      ) : (
        <InventoryTable
          table={table}
          columns={columns}
          rows={listQ.data?.rows || []}
          total={listQ.data?.total}
          loading={listQ.isLoading}
          isAdmin={canWrite}
          view={view}
          onViewChange={changeView}
          onCellSave={onCellSave}
          onBulkSet={canWrite ? onBulkSet : undefined}
          onBulkGroups={canWrite && table === 'accounts' ? onBulkGroups : undefined}
          onChipClick={onChipClick}
          onSecret={(row) => setReveal({ kind: table === 'network' ? 'network' : 'account', row })}
          onAssign={canWrite && table === 'accounts' ? (row) => setAssignRow(row) : undefined}
          onArchive={canWrite && tableMeta?.archivable ? onArchive : undefined}
          onRestore={canWrite && tableMeta?.archivable ? onRestore : undefined}
          showArchived={showArchived}
          toolbar={(
            <>
              {tableMeta?.archivable && (
                <label className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-2 text-xs text-muted-foreground">
                  <Archive className="h-3.5 w-3.5" />
                  <input type="checkbox" aria-label="Show archived" checked={showArchived} onChange={(e) => setSearch({ archived: e.target.checked ? 'true' : null })} />
                  Archived
                </label>
              )}
              <button type="button" onClick={onExport} className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-2 text-xs">
                <Download className="h-3.5 w-3.5" /> Export CSV
              </button>
              {importable && (
                <button type="button" onClick={() => setImportOpen(true)} className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-2 text-xs">
                  <Upload className="h-3.5 w-3.5" /> Import CSV
                </button>
              )}
              {canWrite && (
                <button type="button" onClick={() => setCreateOpen(true)} className="inline-flex h-8 items-center gap-1 rounded-md bg-primary px-2.5 text-xs font-medium text-primary-foreground">
                  <Plus className="h-3.5 w-3.5" /> Add
                </button>
              )}
            </>
          )}
        />
      )}

      <RevealDialog
        open={!!reveal}
        onOpenChange={(o) => { if (!o) { setReveal(null); refetch(); } }}
        kind={reveal?.kind}
        row={reveal?.row}
        isAdmin={isAdmin}
        keyConfigured={meta?.keyConfigured}
        fields={reveal?.kind === 'network' ? meta?.networkSecretFields : meta?.accountSecretFields}
      />
      <AssignDialog open={!!assignRow} onOpenChange={(o) => { if (!o) setAssignRow(null); }} account={assignRow}
        onDone={() => refetch()} />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} table={table} importKey={tableMeta?.importKey}
        onDone={(r) => {
          toast.success(`Imported: ${r.created} new, ${r.updated} updated`);
          const warn = placementWarningText(r);
          if (warn) toast.warning(warn, { duration: 15000 });
          refetch();
        }} />
      <CreateDialog open={createOpen} onOpenChange={setCreateOpen} table={table} fields={tableMeta?.fields} columns={columns}
        onCreated={(row) => {
          toast.success(`Created ${row.label}`);
          const warn = placementWarningText(row);
          if (warn) toast.warning(warn, { duration: 15000 });
          refetch();
        }} />
    </div>
  );
}
